import path from "path";
import { findPackageJson, safeReadJson } from "../utils/files.js";
import { loadPluginsForProject } from "../plugins/loader.js";
import { analyzeDependencies } from "./dependencyAnalyzer.js";
import { CompatibilityEngine } from "./compatibilityEngine.js";
import { computeHealthScore, partitionIssues } from "./healthScorer.js";
import { AnalysisResult } from "./models.js";

/**
 * Main orchestrator for project analysis.
 *
 * Coordinates:
 *  1. Dependency analysis
 *  2. Plugin loading
 *  3. Compatibility engine
 *  4. Health scoring
 */
export async function runAnalysis(cwd: string): Promise<AnalysisResult> {
  const packageJsonPath = findPackageJson(cwd);
  if (!packageJsonPath) {
    throw new Error("package.json not found in the current directory tree.");
  }

  const pkg = safeReadJson(packageJsonPath);
  if (!pkg) {
    throw new Error("package.json is corrupted or unreadable.");
  }

  const projectRoot = path.dirname(packageJsonPath);

  // 1. Dependency analysis
  const depAnalysis = analyzeDependencies(projectRoot, pkg as Record<string, unknown>);

  // 2. Load plugins for this project
  const plugins = await loadPluginsForProject(projectRoot);

  // 3. Run compatibility engine
  const engine = new CompatibilityEngine(projectRoot, depAnalysis, plugins);
  const issues = await engine.run();

  // 4. Health scoring
  const score = computeHealthScore(issues, depAnalysis.nativeModules.length);
  const { warnings, errors, info } = partitionIssues(issues);

  return {
    score,
    dependencies: depAnalysis.totalDependencies,
    nativeModules: depAnalysis.nativeModules.length,
    nativeModuleNames: depAnalysis.nativeModules,
    plugins: plugins.length,
    pluginNames: plugins.map((p) => p.packageName),
    reactNativeVersion: depAnalysis.reactNativeVersion,
    warnings,
    errors,
    info,
  };
}
